import { useEffect, useState } from "react"
import { Button, Card, Form } from "react-bootstrap"
import { EngagementService } from "@services/engagements/engagementService"

const MAX_DAYS = 30
const SINCE_LAST = "since-last"

function formatRelative(iso: string | null): string {
  if (!iso) return "Never"
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return "Never"
  const diffMs = Date.now() - then
  const mins = Math.floor(diffMs / 60_000)
  if (mins < 1) return "Just now"
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function daysSinceLastSync(iso: string | null): { days: number; clamp: "none" | "max" | "never" } {
  if (!iso) return { days: MAX_DAYS, clamp: "never" }
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return { days: MAX_DAYS, clamp: "never" }
  const diffMs = Math.max(0, Date.now() - then)
  const raw = Math.max(1, Math.ceil(diffMs / 86_400_000))
  if (raw > MAX_DAYS) return { days: MAX_DAYS, clamp: "max" }
  return { days: raw, clamp: "none" }
}

const SyncPerformance = () => {
  const [syncLoading, setSyncLoading] = useState(false)
  const [syncSuccess, setSyncSuccess] = useState(false)
  const [syncError, setSyncError] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null)
  const [selectedRange, setSelectedRange] = useState<string>(SINCE_LAST)

  const refreshLastSynced = async () => {
    try {
      const result = await new EngagementService().getLastPerformanceImport()
      setLastSyncedAt(result.last_synced_at)
    } catch (err) {
      console.error("Failed to load last sync:", err)
    }
  }

  useEffect(() => {
    refreshLastSynced()
  }, [])

  const sinceLast = daysSinceLastSync(lastSyncedAt)
  const resolvedDays = selectedRange === SINCE_LAST ? sinceLast.days : Number(selectedRange)

  let clampNote: string | null = null
  if (selectedRange === SINCE_LAST && sinceLast.clamp === "never") {
    clampNote = "No prior sync — pulling the last 30 days"
  } else if (selectedRange === SINCE_LAST && sinceLast.clamp === "max") {
    clampNote = "Last sync was over 30 days ago — pulling the last 30 days"
  }

  const handleSync = async () => {
    setSyncLoading(true)
    try {
      await new EngagementService().importPerformance(resolvedDays)
      setSyncLoading(false)
      setSyncSuccess(true)
      setTimeout(() => setSyncSuccess(false), 2000)
      refreshLastSynced()
    } catch {
      setSyncLoading(false)
      setSyncError(true)
      setTimeout(() => setSyncError(false), 2000)
    }
  }

  return (
    <Card className="custom-card overflow-hidden h-100 border-0 mb-0">
      <Card.Body className="p-3">
        <div className="d-flex align-items-center gap-3 mb-2">
          <Button
            variant={syncSuccess ? "success" : syncError ? "danger" : "outline-primary"}
            size="sm"
            onClick={handleSync}
            disabled={syncLoading}
            className={`btn-transition ${syncSuccess ? "btn-outline-success-animate" : ""} ${syncError ? "btn-error-animate" : ""}`}
          >
            <i className={`bi ${syncSuccess ? "bi-check-circle icon-fade-in" : syncError ? "bi-x-circle icon-fade-in" : syncLoading ? "bi-arrow-repeat icon-spin" : "bi-arrow-repeat"} me-1`} />
            {syncSuccess ? "Job Created" : syncError ? "Failed!" : syncLoading ? "Creating..." : "Sync Performance"}
          </Button>
          <span className="fs-13 text-muted">
            Last synced: {formatRelative(lastSyncedAt)}
          </span>
        </div>
        <div className="d-flex align-items-center gap-3">
          <Form.Select
            size="sm"
            className="w-auto"
            value={selectedRange}
            onChange={(e) => setSelectedRange(e.target.value)}
            disabled={syncLoading}
          >
            <option value={SINCE_LAST}>Since last sync</option>
            <option value="1">1 day</option>
            <option value="3">3 days</option>
            <option value="7">7 days</option>
            <option value="14">14 days</option>
            <option value="30">30 days</option>
          </Form.Select>
          <span className="fs-13 text-muted d-inline-flex align-items-center gap-1">
            <i className="bi bi-calendar-range" />
            Range
          </span>
        </div>
        {clampNote && (
          <small className="text-muted d-block mt-1">{clampNote}</small>
        )}
      </Card.Body>
    </Card>
  )
}

export default SyncPerformance
